import type { Observable } from 'rxjs';
import { of, merge } from 'rxjs';
import { concatMap, reduce, map } from 'rxjs/operators';

import type { RequestResponse } from 'app/utils/request';
import { request } from 'app/utils/request';
import { getPandapiUrl, getPandapiHeaders } from 'app/utils/api';
import { asModel, compareStrings, createArray } from 'app/utils/array';
import { getTodayPeriod } from 'app/utils/date';
import { getAvailableVideogames, isPandascoreVideogame } from 'app/utils/videogame';
import { assertNever } from 'app/utils/types';
import { VIDEOGAME_EVENT_LIST_ORDER } from 'app/config/videogames.config';

import type {
  Tournament,
  EventTreeValue,
  EventTreeChangeAction,
  EventTreeTournamentValue,
  EventTreeVideogameValue,
  EventTreeSerieValue
} from './types';

const WITHOUT_BLUEPRINT_VALUE = -1;
const PAGE_SIZE = 100;

function getTodayUnix() {
  const date = new Date();
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  const timestamp = date.getTime();

  return Math.round(timestamp / 1000);
}

function getNbCompetitions(response: RequestResponse<any>): number {
  if (!response.headers || !response.headers['x-total']) {
    return 0;
  }

  const value = response.headers['x-total'];
  const total = parseInt(value, 10);
  if (isNaN(total)) {
    return 0;
  }

  return total;
}

function sortByTier(
  tierA: { tier: Tier | null; label: string },
  tierB: { tier: Tier | null; label: string }
) {
  if (tierA.tier === tierB.tier) {
    return compareStrings(tierA.label, tierB.label);
  }

  // null tiers are always at bottom
  if (tierA.tier === null) {
    return 1;
  }
  if (tierB.tier === null) {
    return -1;
  }

  // 's' tiers are always at top
  if (tierA.tier === 's') {
    return -1;
  }
  if (tierB.tier === 's') {
    return 1;
  }

  return compareStrings(tierA.tier, tierB.tier);
}

function getInitialTreeValue(
  tournaments: Tournament[],
  videogameList: VideogameSlug[] | undefined
): EventTreeValue {
  const videogames: EventTreeVideogameValue[] = getAvailableVideogames(videogameList).map(
    (videogame, index) => {
      const videogamTournaments = tournaments.filter(
        tournament => tournament.videogame.slug === videogame.slug
      );

      const seriesId = [...new Set(videogamTournaments.map(t => t.serie?.id).filter(Boolean))];
      const series: EventTreeSerieValue[] = seriesId
        .map((serieId, index) => {
          const serieTournaments = videogamTournaments.filter(t => t.serie?.id === serieId);

          const tournaments: EventTreeTournamentValue[] = serieTournaments
            .map(tournament => ({
              id: tournament.id,
              label: tournament.name,
              tier: tournament.tier,
              selected: false
            }))
            .sort(sortByTier);

          const { serie, league } = serieTournaments[0];

          return {
            id: serie?.id ?? index,
            label: [league?.name, serie?.full_name].filter(Boolean).join(' / ') || '',
            selected: false,
            tournaments,
            tier: serie?.tier || tournaments[0].tier
          };
        })
        .sort((a, b) =>
          sortByTier(
            { tier: a.tier, label: a.label.toLowerCase() },
            { tier: b.tier, label: b.label.toLowerCase() }
          )
        );

      return {
        key: index + 1,
        label: videogame.name,
        slug: videogame.slug,
        selected: false,
        series
      };
    }
  );

  return { videogames };
}

function updateTreeValue(value: EventTreeValue, action: EventTreeChangeAction): EventTreeValue {
  switch (action.type) {
    case 'EVENT_TREE_SELECT_ALL': {
      const hasSelection = value.videogames.some(
        videogame =>
          videogame.selected ||
          videogame.series.some(serie => serie.tournaments.some(tournament => tournament.selected))
      );

      // we update value only if there are some videogames or tournaments selected
      if (hasSelection) {
        const videogames = value.videogames.map(videogame => ({
          ...videogame,
          selected: false,
          series: videogame.series.map(serie => ({
            ...serie,
            tournaments: serie.tournaments.map(t => ({ ...t, selected: false }))
          }))
        }));

        return { videogames };
      }

      return value;
    }
    case 'EVENT_TREE_TOGGLE_VIDEOGAME': {
      const videogames = value.videogames.map(videogame => {
        if (videogame.key === action.payload) {
          const selected = !videogame.selected;
          return {
            ...videogame,
            selected,
            series: videogame.series.map(serie => ({
              ...serie,
              selected,
              tournaments: serie.tournaments.map(t => ({ ...t, selected }))
            }))
          };
        }

        return videogame;
      });

      return { videogames };
    }
    case 'EVENT_TREE_TOGGLE_SERIE': {
      const videogames = value.videogames.map(videogame => {
        const series = videogame.series.map(serie => {
          if (serie.id === action.payload) {
            const selected = !serie.selected;
            return {
              ...serie,
              selected,
              tournaments: serie.tournaments.map(tournament => ({ ...tournament, selected }))
            };
          }

          return serie;
        });

        const selected = series.length > 0 && series.every(serie => serie.selected);
        return {
          ...videogame,
          selected,
          series
        };
      });

      return { videogames };
    }
    case 'EVENT_TREE_TOGGLE_TOURNAMENT': {
      const videogames = value.videogames.map(videogame => {
        const series = videogame.series.map(serie => {
          const tournaments = serie.tournaments.map(tournament => {
            if (tournament.id === action.payload) {
              return { ...tournament, selected: !tournament.selected };
            }
            return tournament;
          });
          const selected = tournaments.length > 0 && tournaments.every(serie => serie.selected);

          return {
            ...serie,
            selected,
            tournaments
          };
        });

        const selected = series.length > 0 && series.every(serie => serie.selected);
        return {
          ...videogame,
          selected,
          series
        };
      });

      return { videogames };
    }
    default:
      assertNever(action);
  }
}

/**
 * return the tournaments with id, name and video game slug only if tournament from the same series are selected
 */
function getSeriesSelectedTournaments(
  selectedTournaments: EventTreeValue
): { id: number; name: string; videogame: VideogameSlug; tier: Tier | null }[] {
  const selectedVideogameSeries = selectedTournaments.videogames.filter(
    v =>
      v.series.filter(series => series.selected || series.tournaments.some(t => t.selected))
        .length === 1
  );
  const selectedVideogame = selectedVideogameSeries.at(0);

  if (selectedVideogameSeries.length === 1 && selectedVideogame) {
    const selectedSeries = selectedVideogame.series.find(
      series => series.selected || series.tournaments.some(t => t.selected)
    );
    if (selectedSeries) {
      const tournaments = selectedSeries.tournaments
        .filter(t => t.selected)
        .map(tournament => ({
          id: tournament.id,
          name: tournament.label,
          videogame: selectedVideogame.slug,
          tier: tournament.tier
        }));
      return tournaments;
    }
  }

  return [];
}

function createFetchCompetitions(
  videogameList: VideogameSlug[] | undefined
): Observable<EventTreeValue> {
  const today = getTodayUnix();
  const url = getPandapiUrl('/betting/tournaments');
  const query = {
    unfinished_at: today.toString(),
    'page[size]': `${PAGE_SIZE}`
  };
  const headers = getPandapiHeaders();

  return request<Tournament[]>({ url, query, headers }, { useCache: true }).pipe(
    concatMap(firstResponse => {
      const total = getNbCompetitions(firstResponse);
      const nbNextPages = Math.ceil(Math.max(0, total / PAGE_SIZE - 1));

      const nextPageRequests = createArray(nbNextPages).map((_, index) => {
        const pageQuery = {
          ...query,
          'page[number]': `${index + 2}`
        };
        return request<Tournament[]>({ url, query: pageQuery, headers }, { useCache: true });
      });

      return merge(of(firstResponse), ...nextPageRequests);
    }),
    reduce(
      (acc: Tournament[], response: RequestResponse<Tournament[]>) => acc.concat(response.data!),
      []
    ),
    map(value => getInitialTreeValue(value, videogameList))
  );
}

const getFilteredAndSortedEventTree = (
  data: EventTreeValue,
  displayExternalProvidersMatches: boolean
) => ({
  videogames: data.videogames
    .filter(videogame => displayExternalProvidersMatches || isPandascoreVideogame(videogame.slug))
    .sort((a, b) => {
      const aHasTournaments = a.series.some(serie => serie.tournaments.length > 0);
      const bHasTournaments = b.series.some(serie => serie.tournaments.length > 0);

      if (aHasTournaments && !bHasTournaments) {
        return -1;
      }
      if (!aHasTournaments && bHasTournaments) {
        return 1;
      }

      return compareStrings(a.label, b.label);
    })
    .sort(asModel(VIDEOGAME_EVENT_LIST_ORDER, videogame => videogame.slug))
});

export {
  WITHOUT_BLUEPRINT_VALUE,
  getInitialTreeValue,
  updateTreeValue,
  getSeriesSelectedTournaments,
  createFetchCompetitions,
  getTodayPeriod,
  getFilteredAndSortedEventTree
};
